import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    RETAIL_REG_ORDERED_OBJECT_STEPS,
    RETAIL_REG_ORDERED_STEPS
} from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/components/retailReg/constants';
import { useIsSlotReg } from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/hooks/useIsSlotReg';
import { useRegistrationContext } from '@interlayer/js/app/components/main/Registration/hocs/context';
import { useNavigate } from '@interlayer/js/app/hooks/navigate/useNavigate/useNavigate';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import {
    setIsManuallyAddress,
    setRegistrationNameStep,
    setRegistrationRetailWrapperType
} from '@interlayer/js/app/store/reducers/Registration/actions';
import { STEP_NAMES } from '@interlayer/js/app/store/reducers/Registration/constants';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import { TRetailRegSteps } from '../types';

const useNextStep = (): STEP_NAMES | undefined => {
    const { registrationNameStep } = useRegistrationSelector();

    return RETAIL_REG_ORDERED_STEPS[RETAIL_REG_ORDERED_OBJECT_STEPS[registrationNameStep as TRetailRegSteps] + 1];
};

export const usePrevStep = (): STEP_NAMES | undefined => {
    const { registrationNameStep } = useRegistrationSelector();

    return RETAIL_REG_ORDERED_STEPS[RETAIL_REG_ORDERED_OBJECT_STEPS[registrationNameStep as TRetailRegSteps] - 1];
};

export const useRetailRegPagination = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const nextStep = useNextStep();
    const prevStep = usePrevStep();
    const isSlotReg = useIsSlotReg();
    const { isRetailWrapper } = useRegistrationContext();
    const { isManuallyAddress } = useRegistrationSelector();
    const [
        dTagRetailWelcomePage = '/retail-welcome',
        dTagSlotWelcomePage = '/slot-welcome'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.retailWelcomePage,
        DYNAMIC_TAG_NAMES.slotWelcomePage
    ]);

    const handlerNextStep = useCallback(() => {
        if (!nextStep) {
            return;
        }

        dispatch(setRegistrationNameStep(nextStep));
    }, [dispatch, nextStep]);

    const handlerPrevStep = useCallback(() => {
        if (isManuallyAddress) {
            dispatch(setIsManuallyAddress(false));
            return;
        }

        if (!prevStep) {
            if (isRetailWrapper) {
                dispatch(setRegistrationRetailWrapperType(''));
            } else {
                navigate(isSlotReg ? dTagSlotWelcomePage : dTagRetailWelcomePage);
            }

            return;
        }

        dispatch(setRegistrationNameStep(prevStep));
    }, [isManuallyAddress, prevStep, dispatch, isRetailWrapper, navigate, isSlotReg, dTagSlotWelcomePage, dTagRetailWelcomePage]);

    const handlerNavigateStep = useCallback((stepIndex) => {
        dispatch(setRegistrationNameStep(RETAIL_REG_ORDERED_STEPS[stepIndex]));
    }, [dispatch]);

    return useMemo(() => ({
        handlerNextStep,
        handlerPrevStep,
        handlerNavigateStep
    }), [handlerNavigateStep, handlerNextStep, handlerPrevStep]);
};

